/**
 * cdc-boost-infinite-loader.js
 *
 * Visual feedback while Boost's infinite-scroll pagination fetches the next batch
 * of products on the collection / search grid. Boost still owns the fetch and the
 * product injection — we only surface a 3-dot loader while a batch is in flight.
 *
 *  - SHOW: Boost's (hidden) infinite-scroll target — the same anchor Boost observes
 *    to trigger a fetch — enters the viewport AND more products remain to load.
 *  - HIDE: boostPLPProductsLoaded fires, OR the grid actually gains product items
 *    (MutationObserver fallback — Boost doesn't always re-fire the event on append).
 *  - END OF COLLECTION: rendered product count >= data-total-product → never shown.
 *
 * Lives in the theme (like cdc-boost-filter.js) so it can read Boost's rendered DOM
 * and listen to Boost's lifecycle events.
 */
(function () {
  'use strict';

  var LOADER_ID = 'cdc-infinite-loader';
  var TARGET_SELECTOR = '.boost-sd__pagination-infinite-scroll-container-target';
  var LIST_SELECTOR = '.boost-sd__product-list';
  // Real products only — exclude banners and bundle placeholders so the count
  // matches data-total-product (which counts products, not banners/bundles).
  var PRODUCT_SELECTOR =
    '.boost-sd__product-item' +
    ':not(.boost-sd__product-item--banner)' +
    ':not(.boost-sd__product-item--embedded-bundle)' +
    ':not(.boost-sd__product-item--volume-bundle)';
  var SAFETY_MS = 12000;

  var safetyTimeout = null;
  var observedTarget = null;
  var io = null;

  function getList() {
    return document.querySelector(LIST_SELECTOR);
  }

  // True while the collection still has un-rendered products. On the last page
  // rendered >= total, so the loader is never shown at the end of the collection.
  function hasMore() {
    var list = getList();
    if (!list) return false;
    var total = parseInt(list.getAttribute('data-total-product'), 10);
    if (!total) return false;
    return list.querySelectorAll(PRODUCT_SELECTOR).length < total;
  }

  function ensureLoader() {
    var loader = document.getElementById(LOADER_ID);
    if (!loader) {
      loader = document.createElement('div');
      loader.id = LOADER_ID;
      loader.className = 'cdc-infinite-loader';
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-label', 'Loading more products');
      loader.setAttribute('aria-hidden', 'true');
      loader.innerHTML =
        '<span class="cdc-infinite-loader__dot"></span>' +
        '<span class="cdc-infinite-loader__dot"></span>' +
        '<span class="cdc-infinite-loader__dot"></span>';
    }
    // Keep it centered directly below the (possibly re-rendered) product grid.
    var list = getList();
    if (list && loader.previousElementSibling !== list) {
      list.parentNode.insertBefore(loader, list.nextSibling);
    }
    return loader;
  }

  function showLoader() {
    if (loaderVisible()) return;
    var loader = ensureLoader();
    loader.classList.add('is-visible');
    loader.setAttribute('aria-hidden', 'false');
    // Safety net: drop the loader if nothing renders / no event fires.
    clearTimeout(safetyTimeout);
    safetyTimeout = setTimeout(hideLoader, SAFETY_MS);
  }

  function hideLoader() {
    clearTimeout(safetyTimeout);
    var loader = document.getElementById(LOADER_ID);
    if (loader) {
      loader.classList.remove('is-visible');
      loader.setAttribute('aria-hidden', 'true');
    }
  }

  function loaderVisible() {
    var loader = document.getElementById(LOADER_ID);
    return !!(loader && loader.classList.contains('is-visible'));
  }

  function onTargetIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting && hasMore()) {
        showLoader();
        return;
      }
    }
  }

  // (Re)bind the IntersectionObserver to the current target. Boost replaces the
  // target node when it rebuilds the grid (filter/sort), so re-acquire on changes.
  function attachTargetObserver() {
    var target = document.querySelector(TARGET_SELECTOR);
    if (!target || target === observedTarget) return;
    if (io) io.disconnect();
    observedTarget = target;
    io = new IntersectionObserver(onTargetIntersect, {
      // Fire a little before the anchor is fully on-screen so feedback is immediate
      // the moment the scroll threshold is reached.
      rootMargin: '200px 0px',
      threshold: 0,
    });
    io.observe(target);
  }

  // A mutation batch ADDED a product item (or the list itself) → new results are in.
  function gridGainedProducts(records) {
    return records.some(function (record) {
      return Array.prototype.some.call(record.addedNodes, function (node) {
        if (!node || node.nodeType !== 1) return false;
        return (node.matches && node.matches('.boost-sd__product-item')) ||
          (node.querySelector && !!node.querySelector('.boost-sd__product-item'));
      });
    });
  }

  function start() {
    attachTargetObserver();

    // Boost's own "products loaded" lifecycle events — primary hide signal.
    document.addEventListener('boostPLPProductsLoaded', function () {
      hideLoader();
      attachTargetObserver();
    });
    document.addEventListener('boostSDAppLoaded', function () {
      hideLoader();
      attachTargetObserver();
    });

    // Boost rebuilds the grid (filter/sort) or appends to it (infinite scroll) without
    // always re-firing the event — re-acquire the target and hide the loader the moment
    // new product items actually land in the grid.
    new MutationObserver(function (records) {
      attachTargetObserver();
      if (loaderVisible() && gridGainedProducts(records)) {
        hideLoader();
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
